import React from 'react';

import { Checkbox, Grid } from '@cowbell/ui';

import { Show } from '../../../Show';

import type { Languages } from '../../../../i18n/i18n.language-config';
import { OrgAddressFields } from './OrgAddressFields';
import { OrgCompletionBadge } from './OrgCompletionBadge';
import { OrgPanel } from './OrgPanel';
import classes from './OrgMailingAddressPanel.module.css';
import type { OrgAddressFormApi } from './useOrgAddressForm';

interface OrgMailingAddressPanelProps {
  readonly form: OrgAddressFormApi;
}

/**
 * The Mailing Address panel: a shortcut to reuse the business address, or the
 * mailing fields in full.
 *
 * <p>Shares the business panel's form -- the shortcut writes across both
 * addresses -- and renders nothing at all where the mailing-address feature is
 * off, which is how the old accordion behaved.
 */
export const OrgMailingAddressPanel = ({
  form,
}: OrgMailingAddressPanelProps) => {
  if (!form.mailingAddressFeatureOn) {
    return null;
  }

  const { businessAddressSummary } = summarise(form);

  return (
    <OrgPanel
      id="mailing"
      title="Mailing address"
      description="Where policy documents and correspondence are sent."
      status={<OrgCompletionBadge missing={form.mailingMissingCount} />}
    >
      <div className={classes.sameAsRow}>
        <Checkbox
          name="isSameAddress"
          label="Same as business address"
          onClick={form.handleSameAddress}
          disabled={form.areAnyPhysicalAddressFieldsEmpty}
          checked={form.isSameAddress}
        />
        <p className={classes.sameAsCopy}>
          {form.isSameAddress
            ? businessAddressSummary
            : 'Turn this on to reuse the address above.'}
        </p>
      </div>

      <Show when={!form.isSameAddress}>
        <Grid gutter="md">
          <OrgAddressFields
            language={form.language as Languages}
            layout={form.gridLayoutSettings}
            fieldPrefix="mailingAddress."
            disabledFields={form.disabledFields}
          />
        </Grid>
      </Show>
    </OrgPanel>
  );
};

/** The one-line echo of the business address the design shows when mirrored. */
function summarise(form: OrgAddressFormApi) {
  const values = form.methods.getValues();
  const parts = [values.address1, values.city, values.state, values.zipCode]
    .filter((part) => typeof part === 'string' && part.trim() !== '')
    .join(', ');

  return {
    businessAddressSummary: parts || 'Reusing the address above.',
  };
}

export default OrgMailingAddressPanel;
